import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';
import {
  buildEventOptionParticipationIndex,
  createEmptyEventOptionParticipationIndex,
  type EventOptionParticipationIndex,
} from '../domain/options/participation';

type ClientOption = { supabase?: SupabaseClient };

export type OptionSummary = {
  groups: number;
  pending: number;
  participation: EventOptionParticipationIndex;
};
export type CustomerDiscountInfo = { enabled: boolean; percent: number | null };
export type LinkedFulfillmentOrderCounts = { transferOrderCount: number; purchaseOrderCount: number };

export async function fetchOptionSummary(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: OptionSummary; error: PostgrestError | null }> {
  if (!eventId) {
    return {
      data: {
        groups: 0,
        pending: 0,
        participation: createEmptyEventOptionParticipationIndex(),
      },
      error: null,
    };
  }
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('event_option_groups')
    .select(`
      event_option_group_id,
      title,
      event_option_choices(
        event_option_choice_id,
        label,
        status,
        event_option_links(target_type,target_id)
      )
    `)
    .eq('event_id', eventId);
  if (error) {
    return {
      data: {
        groups: 0,
        pending: 0,
        participation: createEmptyEventOptionParticipationIndex(),
      },
      error,
    };
  }
  return { data: summarizeOptionGroupRows(data), error: null };
}

export const summarizeOptionGroupRows = (data: unknown): OptionSummary => {
  let groups = 0;
  let pending = 0;
  if (Array.isArray(data)) {
    groups = data.length;
    data.forEach(groupValue => {
      const group = groupValue && typeof groupValue === 'object'
        ? groupValue as Record<string, unknown>
        : {};
      const choices = Array.isArray(group.event_option_choices) ? group.event_option_choices : [];
      choices.forEach(choiceValue => {
        const choice = choiceValue && typeof choiceValue === 'object'
          ? choiceValue as Record<string, unknown>
          : {};
        const status = typeof choice.status === 'string' ? choice.status : '';
        if (status === 'draft' || status === 'submitted') {
          pending += 1;
        }
      });
    });
  }
  return {
    groups,
    pending,
    participation: buildEventOptionParticipationIndex(data),
  };
};

export async function fetchLinkedFulfillmentOrderCounts(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: LinkedFulfillmentOrderCounts; error: PostgrestError | null }> {
  if (!eventId) {
    return { data: { transferOrderCount: 0, purchaseOrderCount: 0 }, error: null };
  }
  const supabase = options?.supabase ?? getSupabaseClient();
  let firstError: PostgrestError | null = null;

  const transferCountQuery = await supabase
    .from('transfer_order_events_link')
    .select('link_id', { count: 'exact', head: true })
    .eq('event_id', eventId);
  if (transferCountQuery.error && !firstError) firstError = transferCountQuery.error;

  let purchaseCount = 0;
  const demandCountQuery = await supabase
    .from('event_line_fulfillment_demand')
    .select('demand_id,event_lines!inner(event_id)', { count: 'exact', head: true })
    .in('fulfillment_type', ['purchase', 'cross_rental'])
    .eq('event_lines.event_id', eventId);

  if (demandCountQuery.error) {
    // Fallback for environments where the new sidecar table is not deployed yet.
    const purchaseCountQuery = await supabase
      .from('subrentals')
      .select('subrental_id', { count: 'exact', head: true })
      .eq('event_id', eventId);
    if (purchaseCountQuery.error && !firstError) firstError = purchaseCountQuery.error;
    purchaseCount = typeof purchaseCountQuery.count === 'number' ? purchaseCountQuery.count : 0;
  } else {
    purchaseCount = typeof demandCountQuery.count === 'number' ? demandCountQuery.count : 0;
  }

  return {
    data: {
      transferOrderCount: typeof transferCountQuery.count === 'number' ? transferCountQuery.count : 0,
      purchaseOrderCount: purchaseCount,
    },
    error: firstError,
  };
}

export async function fetchCustomerDiscount(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: CustomerDiscountInfo; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const eventQuery = await supabase
    .from('events')
    .select('client_id,bill_to_is_different,bill_to_client_id,apply_customer_discount')
    .eq('id', eventId)
    .maybeSingle();
  if (eventQuery.error) return { data: { enabled: true, percent: null }, error: eventQuery.error };

  const eventRow = eventQuery.data as any;
  const enabled = eventRow?.apply_customer_discount !== false;
  const clientId =
    eventRow?.bill_to_is_different && eventRow?.bill_to_client_id
      ? String(eventRow.bill_to_client_id)
      : eventRow?.client_id
        ? String(eventRow.client_id)
        : null;

  if (!clientId) {
    return { data: { enabled, percent: null }, error: null };
  }

  const clientQuery = await supabase
    .from('clients')
    .select('set_discount_percent')
    .eq('id', clientId)
    .maybeSingle();
  if (clientQuery.error) return { data: { enabled, percent: null }, error: clientQuery.error };
  const percent = typeof clientQuery.data?.set_discount_percent === 'number'
    ? clientQuery.data.set_discount_percent
    : clientQuery.data?.set_discount_percent != null
      ? Number(clientQuery.data.set_discount_percent)
      : null;
  return {
    data: { enabled, percent: Number.isFinite(percent) ? Number(percent) : null },
    error: null,
  };
}

export async function updateApplyCustomerDiscount(
  eventId: string,
  apply: boolean,
  options?: ClientOption,
): Promise<PostgrestError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  
  // Get current user's profile ID for updated_by
  const { data: profileId } = await supabase.rpc('current_user_profile_id').maybeSingle();
  
  const { error } = await supabase.from('events').update({ 
    apply_customer_discount: apply,
    updated_by: profileId || null,
  }).eq('id', eventId);
  return error ?? null;
}

export async function fetchInventoryItemCount(
  locationId: string | null | undefined,
  options?: ClientOption,
): Promise<{ count: number | null; error: PostgrestError | null }> {
  if (!locationId) return { count: null, error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const { count, error } = await supabase
    .from('item_location_stock')
    .select('item_id', { count: 'exact', head: true })
    .eq('location_id', locationId);
  return { count: typeof count === 'number' ? count : null, error: error ?? null };
}
